import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldStar, CheckCircle, Warning, Copy, Devices, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import QRCode from "qrcode";

export default function TwoFactorSetup() {
  const { user, setUser } = useAuth();
  const enabled = !!user?.mfa_enabled;

  const [setupData, setSetupData] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const [sessions, setSessions] = useState([]);

  const refreshSessions = useCallback(
    () => api.get("/auth/sessions").then((r) => setSessions(r.data || [])).catch(() => {}),
    [],
  );
  useEffect(() => { refreshSessions(); }, [refreshSessions]);

  useEffect(() => {
    if (setupData?.otpauth_uri) {
      QRCode.toDataURL(setupData.otpauth_uri, { margin: 1, width: 240 }).then(setQrDataUrl);
    }
    // QRCode is a module import + setQrDataUrl is a React-stable setter — both safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupData]);

  const startSetup = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/auth/2fa/setup");
      setSetupData(res.data);
      setRecoveryCodes(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/auth/2fa/enable", { code: code.trim() });
      setRecoveryCodes(res.data?.recovery_codes || []);
      setSetupData(null);
      setUser({ ...user, mfa_enabled: true });
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode.trim() });
      setUser({ ...user, mfa_enabled: false });
      setDisableCode("");
      toast.success("Two-factor authentication disabled");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <DashboardLayout title="Two-factor authentication" subtitle="Add an extra step at sign-in using an authenticator app.">
      <div className="vs-card p-6 max-w-2xl space-y-5" data-testid="twofa-card">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-[var(--vs-secondary)]/10 grid place-items-center">
            <ShieldStar size={22} weight="duotone" className="text-[var(--vs-secondary)]" />
          </div>
          <div>
            <div className="font-display text-base font-semibold">TOTP via Google Authenticator / Authy</div>
            <div className="text-xs text-[var(--vs-text-secondary)]">
              Status:{" "}
              {enabled ? (
                <Badge variant="outline" className="ml-1 border-[var(--vs-success)]/40 text-[var(--vs-success)]"><CheckCircle size={10} weight="fill" className="mr-1" /> ENABLED</Badge>
              ) : (
                <Badge variant="outline" className="ml-1 border-[var(--vs-warning)]/40 text-[var(--vs-warning)]"><Warning size={10} weight="fill" className="mr-1" /> NOT SET UP</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Setup flow */}
        {!enabled && !setupData && (
          <Button onClick={startSetup} disabled={submitting} data-testid="twofa-start-setup" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
            {submitting ? "Generating…" : "Set up two-factor"}
          </Button>
        )}

        {setupData && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-[240px,1fr] gap-4 items-start">
              {qrDataUrl && (
                <img src={qrDataUrl} alt="Scan in your authenticator" className="rounded-md border border-[var(--vs-border)]" data-testid="twofa-qr" />
              )}
              <div className="text-sm space-y-2">
                <div className="eyebrow">1. Scan the QR</div>
                <p className="text-[var(--vs-text-secondary)]">Open Google Authenticator (or any TOTP app) and scan this QR.</p>
                <div className="vs-divider" />
                <div className="eyebrow">Or enter the secret manually</div>
                <div className="flex items-center gap-2">
                  <code className="px-2 py-1 rounded bg-[var(--vs-bg-subtle)] font-mono text-xs break-all" data-testid="twofa-secret">{setupData.secret}</code>
                  <button onClick={() => copy(setupData.secret)} className="text-[var(--vs-secondary)]"><Copy size={14} /></button>
                </div>
              </div>
            </div>
            <form onSubmit={enable} className="space-y-3">
              <div className="eyebrow">2. Verify with the first 6-digit code</div>
              <input
                data-testid="twofa-enable-code"
                autoFocus
                inputMode="numeric"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full sm:w-48 px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white font-mono text-lg tracking-[0.4em] text-center focus:outline-none focus:border-[var(--vs-secondary)] focus:ring-2 focus:ring-[var(--vs-secondary)]/20"
                placeholder="123456"
              />
              <div>
                <Button type="submit" disabled={submitting || code.length < 6} data-testid="twofa-enable-submit" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
                  {submitting ? "Verifying…" : "Enable two-factor"}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Recovery codes shown once */}
        {recoveryCodes && recoveryCodes.length > 0 && (
          <div className="p-4 rounded-md border border-[var(--vs-warning)]/40 bg-[var(--vs-warning)]/10 space-y-3" data-testid="twofa-recovery-codes">
            <div className="font-display text-sm font-semibold flex items-center gap-2">
              <Warning size={14} className="text-[var(--vs-warning)]" /> Save these recovery codes
            </div>
            <p className="text-xs text-[var(--vs-text-secondary)]">Each code is single-use. They are the ONLY way back in if you lose your device. We will not show them again.</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-xs">
              {recoveryCodes.map((c) => (<div key={c} className="px-2 py-1 rounded bg-white border border-[var(--vs-border)]">{c}</div>))}
            </div>
            <Button onClick={() => copy(recoveryCodes.join("\n"))} variant="outline" size="sm" data-testid="twofa-copy-recovery"><Copy size={12} className="mr-1.5" /> Copy all</Button>
          </div>
        )}

        {/* Disable flow */}
        {enabled && (
          <form onSubmit={disable} className="border-t border-[var(--vs-border)] pt-4 space-y-3">
            <div className="eyebrow">Disable two-factor</div>
            <p className="text-xs text-[var(--vs-text-secondary)]">Enter the current 6-digit code from your app to remove 2FA from this account.</p>
            <div className="flex items-center gap-2">
              <input
                data-testid="twofa-disable-code"
                inputMode="numeric"
                maxLength={6}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                className="w-48 px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white font-mono"
                placeholder="123456"
              />
              <Button type="submit" disabled={submitting || disableCode.length < 6} variant="outline" data-testid="twofa-disable-submit">
                Disable
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Active sessions */}
      <div className="vs-card p-6 max-w-2xl space-y-3 mt-4" data-testid="sessions-card">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-[var(--vs-secondary)]/10 grid place-items-center">
            <Devices size={22} weight="duotone" className="text-[var(--vs-secondary)]" />
          </div>
          <div>
            <div className="font-display text-base font-semibold">Active sessions</div>
            <div className="text-xs text-[var(--vs-text-secondary)]">{sessions.length} device{sessions.length !== 1 ? "s" : ""} signed in. Revoking ends that session immediately.</div>
          </div>
        </div>
        <div className="vs-divider" />
        {sessions.length === 0 && <div className="text-sm text-[var(--vs-text-secondary)]">No active refresh tokens — you'll see entries here after your next login.</div>}
        <ul className="space-y-2">
          {sessions.map((s) => (
            <li key={s.jti} className="flex items-center justify-between p-3 rounded-md border border-[var(--vs-border)] bg-[var(--vs-bg-subtle)]" data-testid={`session-row-${s.jti.slice(0,8)}`}>
              <div className="text-xs">
                <div className="font-mono">jti: {s.jti.slice(0, 16)}…</div>
                <div className="text-[var(--vs-text-secondary)]">issued {new Date(s.issued_at).toLocaleString()} · expires {new Date(s.expires_at).toLocaleDateString()}</div>
              </div>
              <Button size="sm" variant="outline" data-testid={`revoke-session-${s.jti.slice(0,8)}`} onClick={async () => {
                try { await api.post(`/auth/sessions/${s.jti}/revoke`); toast.success("Session revoked"); refreshSessions(); }
                catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
              }}><X size={12} className="mr-1" /> Revoke</Button>
            </li>
          ))}
        </ul>
      </div>
    </DashboardLayout>
  );
}
